import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import { CircleNotchIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import type { ImportAgentBundleResponse } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Label } from 'src/components/ui/label';
import { JsonFileTextarea } from 'src/components/common/JsonFileTextarea';
import { useToast } from 'src/context/ToastContext';
import paths from 'src/paths';
import { useImportAgentBundle } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import {
  parseAgentBundleText,
  readAgentBundleFile,
} from 'src/lib/agents/importExport';

interface ImportAgentBundleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Import as a new version of this agent. Omitted → create a new agent. */
  agentDefinitionId?: string;
}

const JSON_PLACEHOLDER = `{
  "formatVersion": 1,
  "agent": { "name": "Portal agent", "systemPrompt": "...", ... },
  "procedures": [ ... ]
}`;

/**
 * Import an `.agent.json` bundle written by an agent's Export menu. On an agent page it becomes
 * that agent's next version; from the agents list it creates a new (inactive) agent. Either way
 * the procedures in the file are matched to this organization's procedures by name and get a
 * new version only where their steps differ.
 */
export function ImportAgentBundleDialog({
  open,
  onOpenChange,
  agentDefinitionId,
}: ImportAgentBundleDialogProps) {
  const gt = useGT();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [jsonText, setJsonText] = useState('');
  const [publish, setPublish] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const importBundle = useImportAgentBundle();
  const intoExisting = agentDefinitionId !== undefined;

  function close() {
    setJsonText('');
    setError(null);
    importBundle.reset();
    onOpenChange(false);
  }

  function handleImport() {
    const parsed = parseAgentBundleText(jsonText);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setError(null);
    importBundle.mutate(
      { bundle: parsed.bundle, agentDefinitionId, publish },
      {
        onSuccess: (result) => {
          showToast(importedToast(gt, result));
          close();
          if (result.created) {
            void navigate({
              to: paths.authenticated.backgroundAgents.definition,
              params: { agentDefinitionId: result.definition.id },
            });
          }
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {intoExisting ? (
              <T context="dialog title, import a JSON file as a new version of this agent">
                Import Agent Version
              </T>
            ) : (
              <T context="dialog title, create a new agent from a JSON file">
                Import Agent
              </T>
            )}
          </DialogTitle>
          <DialogDescription>
            {intoExisting ? (
              <T context="description of importing an agent JSON file as a new version">
                Load a file from an agent's “Export” menu, or paste its JSON. It
                becomes this agent's next version. Procedures in the file update
                the procedures of the same name here (a new version only when
                their steps changed) or are created. Credentials that don't
                exist here keep this agent's own settings.
              </T>
            ) : (
              <T context="description of creating an agent from an exported JSON file">
                Load a file from an agent's “Export” menu, or paste its JSON. A
                new agent is created, inactive, so you can review it before it
                runs. Procedures in the file update the procedures of the same
                name here (a new version only when their steps changed) or are
                created.
              </T>
            )}
          </DialogDescription>
        </DialogHeader>
        <JsonFileTextarea
          value={jsonText}
          onChange={(text) => {
            setJsonText(text);
            setError(null);
            importBundle.reset();
          }}
          onError={setError}
          readFile={readAgentBundleFile}
          placeholder={JSON_PLACEHOLDER}
          loadLabel={
            <T context="button label to load an exported agent JSON file">
              Load from File
            </T>
          }
        />
        {intoExisting && (
          <div className="flex items-start gap-2">
            <Checkbox
              id="publish-imported-agent-version"
              checked={publish}
              onCheckedChange={(checked) => setPublish(checked === true)}
            />
            <Label
              htmlFor="publish-imported-agent-version"
              className="leading-4"
            >
              <T context="checkbox label when importing an agent version from JSON">
                Publish immediately for new agent runs
              </T>
            </Label>
          </div>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        {importBundle.error && (
          <p className="text-sm text-destructive">
            {importBundle.error.message}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            <T context="button, cancel importing an agent from JSON">Cancel</T>
          </Button>
          <Button
            onClick={handleImport}
            disabled={!jsonText.trim() || importBundle.isPending}
          >
            {importBundle.isPending ? (
              <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <UploadSimpleIcon className="mr-2 h-4 w-4" />
            )}
            <T context="button label to import an agent from an exported JSON file">
              Import
            </T>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Success toast; a warning when something from the file could not be applied here. */
function importedToast(
  gt: ReturnType<typeof useGT>,
  result: ImportAgentBundleResponse,
): Parameters<ReturnType<typeof useToast>['showToast']>[0] {
  const changed = result.procedures.filter(
    (procedure) => procedure.outcome !== 'unchanged',
  ).length;
  const title = result.created
    ? gt('Agent imported', {
        context: 'toast title after creating an agent from JSON',
      })
    : gt('Imported as v{version}', {
        context: 'toast title after importing a new agent version from JSON',
        version: String(result.version),
      });
  const summary = gt('{changed} of {total} procedure(s) created or updated.', {
    context: 'toast message after importing an agent from JSON',
    changed: String(changed),
    total: String(result.procedures.length),
  });
  const dropped = [
    ...result.droppedConfigReferences,
    ...result.procedures.flatMap((procedure) =>
      procedure.droppedReferences.map((ref) => `${procedure.name}.${ref}`),
    ),
  ];
  if (dropped.length > 0) {
    return {
      title,
      color: 'warning',
      message: gt(
        '{summary} These references do not exist in this organization and were left unset or unchanged: {refs}.',
        {
          context:
            'toast message after an agent JSON import could not apply credential references',
          summary,
          refs: dropped.join(', '),
        },
      ),
    };
  }
  return { title, color: 'success', message: summary };
}
